/**
 * Llama spit (vanilla LlamaSpitModel on entity/llama/llama_spit.png, 64x32): a
 * plus-shaped cluster of seven 2x2x2 cubes, all mapped to the same sheet corner.
 * Fed by entities/trader/spit.ts `spits.views()` each frame.
 */

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { SpitView } from '../entities/trader/spit';
import { boxGeometry } from './firstPerson';

export class SpitRenderer {
  private readonly group = new THREE.Group();
  private readonly geo: THREE.BufferGeometry;
  private readonly mat: THREE.MeshBasicMaterial;
  private readonly meshes: THREE.Mesh[] = [];

  constructor(scene: THREE.Scene) {
    const tex = new THREE.TextureLoader().load('mc/entity/llama/llama_spit.png');
    tex.magFilter = tex.minFilter = THREE.NearestFilter;
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.generateMipmaps = false;
    this.mat = new THREE.MeshBasicMaterial({ map: tex, alphaTest: 0.1, side: THREE.DoubleSide });
    const cells: [number, number, number][] = [[-4, 0, 0], [0, -4, 0], [0, 0, -4], [0, 0, 0], [2, 0, 0], [0, 2, 0], [0, 0, 2]];
    const parts = cells.map(([x, y, z]) => boxGeometry(0, 0, x, y, z, 2, 2, 2, 0, 64, 32));
    const merged = mergeGeometries(parts.map((g) => (g.index ? g.toNonIndexed() : g)))!;
    merged.translate(-1 / 16, -1 / 16, -1 / 16);
    this.geo = merged;
    scene.add(this.group);
  }

  update(views: readonly SpitView[], alpha: number): void {
    while (this.meshes.length < views.length) {
      const m = new THREE.Mesh(this.geo, this.mat);
      m.frustumCulled = false;
      this.group.add(m);
      this.meshes.push(m);
    }
    this.meshes.forEach((m, i) => {
      const v = views[i];
      m.visible = !!v;
      if (!v) return;
      m.position.set(v.px + (v.x - v.px) * alpha, v.py + (v.y - v.py) * alpha, v.pz + (v.z - v.pz) * alpha);
    });
  }
}
